import React, { createContext, useContext, useState, useEffect } from "react";
import { useOrganizationBranch } from "./../context/OrganizationBranchContext";
import storage from "../utils/storage";
import axiosInstance from "../api/axiosConfig";

const CategoryContext = createContext();

export const useCategory = () => useContext(CategoryContext);

export const CategoryProvider = ({ children }) => {
  const {
    currentOrganization,
    currentBranch,
    organizationMode,
  } = useOrganizationBranch();

  const getStorageKey = () => {
    const orgKey = currentOrganization?._id || "global";
    const branchKey =
      organizationMode === "multi-branch"
        ? currentBranch?._id || "all"
        : "single";
    return `categories_${orgKey}_${branchKey}`;
  };

  const [categories, setCategories] = useState([]);

  // Load categories: fetch from API, fall back to scoped cache on
  // failure so navigating between orgs stays snappy.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    if (!token) {
      setCategories(storage.getJSON(getStorageKey(), []));
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const res = await axiosInstance.get("/categories");
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.categories || [];
        const normalised = list.map((c) => ({ ...c, id: c._id || c.id }));
        if (cancelled) return;
        setCategories(normalised);
        storage.setJSON(getStorageKey(), normalised);
      } catch (e) {
        if (!cancelled) setCategories(storage.getJSON(getStorageKey(), []));
        console.warn("CategoryContext: API fetch failed, using cache:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [currentOrganization?._id, currentBranch?._id, organizationMode]);

  // Persist categories to scoped storage key
  useEffect(() => {
    const key = getStorageKey();
    storage.setJSON(key, categories);
  }, [categories, currentOrganization?._id, currentBranch?._id, organizationMode]);

  // Generate unique category code
  const generateCategoryCode = (organizationId = null) => {
    const prefix = "CAT";
    const existingCodes = categories
      .filter(cat => cat.organizationId === organizationId)
      .map(cat => cat.categoryCode)
      .filter(code => code && code.startsWith(prefix));
    
    let counter = 1;
    let newCode;
    do {
      newCode = `${prefix}-${counter.toString().padStart(3, '0')}`;
      counter++;
    } while (existingCodes.includes(newCode));
    
    return newCode;
  };

  // Get categories by organization
  const getCategoriesByOrganization = (organizationId) => {
    return categories.filter(category => 
      category.organizationId === organizationId || 
      category.organization === organizationId
    );
  };

  // Get parent categories (categories without parent)
  const getParentCategories = (organizationId = null) => {
    const orgCategories = organizationId 
      ? getCategoriesByOrganization(organizationId)
      : categories;
    
    return orgCategories.filter(category => 
      !category.parentCategoryId && category.status !== 'Inactive'
    );
  };

  // Get subcategories of a parent category
  const getSubcategories = (parentCategoryId) => {
    return categories.filter(category => 
      category.parentCategoryId === parentCategoryId && 
      category.status !== 'Inactive'
    );
  };

  // Get category hierarchy (parent + subcategories)
  const getCategoryHierarchy = (organizationId = null) => {
    const parentCategories = getParentCategories(organizationId);
    return parentCategories.map(parent => ({
      ...parent,
      subcategories: getSubcategories(parent.id)
    }));
  };

  // Check if category can be deleted (no subcategories and no products)
  const canDeleteCategory = (categoryId) => {
    const hasSubcategories = categories.some(cat => 
      cat.parentCategoryId === categoryId && cat.status !== 'Inactive'
    );
    // Note: Product count check would be implemented when product integration is added
    return !hasSubcategories;
  };

  const addCategory = (category) => {
    // Ensure organization and branch context on category
    const orgId = category.organizationId || currentOrganization?._id || null;
    const orgName = category.organizationName || currentOrganization?.name;
    const branchId =
      organizationMode === "multi-branch" ? currentBranch?._id || null : null;
    const branchName =
      organizationMode === "multi-branch" ? currentBranch?.name || null : null;

    const newCategory = {
      ...category,
      organizationId: orgId,
      organizationName: orgName,
      branchId: branchId,
      branchName: branchName,
      id: Date.now().toString(),
      categoryCode: category.categoryCode || generateCategoryCode(category.organizationId),
      status: category.status || 'Active',
      categoryType: category.categoryType || 'Inventory',
      createdAt: new Date().toISOString(),
      createdBy: category.createdBy || 'System',
    };
    setCategories([...categories, newCategory]);
    return newCategory;
  };

  const updateCategory = (id, updatedData) => {
    const updatedCategories = categories.map((category) =>
      category.id === id ? { 
        ...category, 
        ...updatedData,
        updatedAt: new Date().toISOString(),
        updatedBy: updatedData.updatedBy || 'System'
      } : category
    );
    setCategories(updatedCategories);
  };

  const deleteCategory = (id) => {
    if (canDeleteCategory(id)) {
      setCategories(categories.filter((category) => category.id !== id));
      return true;
    }
    return false;
  };

  const getCategory = (id) => {
    return categories.find((category) => category.id === id);
  };

  // Get categories by type
  const getCategoriesByType = (type, organizationId = null) => {
    const orgCategories = organizationId 
      ? getCategoriesByOrganization(organizationId)
      : categories;
    
    return orgCategories.filter(category => 
      category.categoryType === type && category.status !== 'Inactive'
    );
  };

  // Search categories with filters
  const searchCategories = (searchTerm, filters = {}) => {
    let filteredCategories = [...categories];

    // Apply organization filter
    if (filters.organizationId) {
      filteredCategories = filteredCategories.filter(category => 
        category.organizationId === filters.organizationId
      );
    }

    // Apply category type filter
    if (filters.categoryType) {
      filteredCategories = filteredCategories.filter(category => 
        category.categoryType === filters.categoryType
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredCategories = filteredCategories.filter(category => 
        category.status === filters.status
      );
    }

    // Apply parent category filter
    if (filters.parentCategoryId !== undefined) {
      if (filters.parentCategoryId === null) {
        // Show only parent categories
        filteredCategories = filteredCategories.filter(category => 
          !category.parentCategoryId
        );
      } else {
        // Show subcategories of specific parent
        filteredCategories = filteredCategories.filter(category => 
          category.parentCategoryId === filters.parentCategoryId
        );
      }
    }

    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredCategories = filteredCategories.filter(category => 
        category.name.toLowerCase().includes(term) ||
        category.categoryCode.toLowerCase().includes(term) ||
        (category.description && category.description.toLowerCase().includes(term))
      );
    }

    return filteredCategories;
  };

  return (
    <CategoryContext.Provider
      value={{
        categories,
        addCategory,
        updateCategory,
        deleteCategory,
        getCategory,
        generateCategoryCode,
        getCategoriesByOrganization,
        getParentCategories,
        getSubcategories,
        getCategoryHierarchy,
        canDeleteCategory,
        getCategoriesByType,
        searchCategories,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export default CategoryContext;
